import { Annotation, Compartment, EditorState, Transaction, type Extension } from '@codemirror/state';
import {
  EditorView,
  crosshairCursor,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers,
  placeholder,
  rectangularSelection,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab, redo, undo } from '@codemirror/commands';
import {
  HighlightStyle,
  LanguageDescription,
  bracketMatching,
  foldGutter,
  foldKeymap,
  indentOnInput,
  syntaxHighlighting,
} from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { lintKeymap } from '@codemirror/lint';
import { getChunks, getOriginalDoc, goToNextChunk, goToPreviousChunk, unifiedMergeView } from '@codemirror/merge';
import { languages } from '@codemirror/language-data';
import { languageInfo } from '../languages';

export interface EditorSelection {
  from: number;
  to: number;
  text: string;
  fromLine: number;
  toLine: number;
}

export interface ReviewState {
  reviewing: boolean;
  /** Changes still undecided. A new file counts as one. */
  remaining: number;
  /** The editor was empty, so the proposal is shown as plain code rather than an all-green diff. */
  newFile: boolean;
  /** The model is still writing the proposal; the editor is read-only and follows the end. */
  streaming: boolean;
  /** The user caused this change (a per-change Accept or Reject, or typing), not Cue itself. */
  byUser: boolean;
}

export interface EditorCallbacks {
  /** Only user edits. Programmatic changes (switching cards, showing a proposal) are not reported. */
  onChange(doc: string): void;
  onSelectionChange(selection: EditorSelection | null): void;
  onReviewChange(state: ReviewState): void;
  onRun(): void;
  onInlineEdit(): void;
}

export interface EditorApi {
  getDoc(): string;
  /** Replace the document and leave any review. Used when switching cards. */
  setDoc(doc: string): void;
  getSelection(): EditorSelection | null;
  setLanguage(id: string): Promise<void>;
  setTheme(dark: boolean): void;
  /** Show `proposed` as the document for review against `original`. */
  startReview(original: string, proposed: string): void;
  /** Show a proposal while it is still being written. Call repeatedly as more arrives. */
  streamReview(original: string, partial: string): void;
  /** Drop a streaming preview and put `restore` back, outside undo history. */
  cancelStream(restore: string): void;
  /** Leave review mode keeping either the current document or the original one. Returns the document. */
  endReview(keep: 'current' | 'original'): string;
  isReviewing(): boolean;
  /** Measure again after the editor was hidden (a tab that was not showing). */
  refresh(): void;
  nextChunk(): void;
  previousChunk(): void;
  focus(): void;
  undo(): void;
  redo(): void;
  destroy(): void;
}

/** Read-only while a proposal streams in, announced to assistive technology as well. */
const streamingReadOnly: Extension = [
  EditorState.readOnly.of(true),
  EditorView.contentAttributes.of({ 'aria-readonly': 'true' }),
];

/** Marks transactions Cue makes itself, so they are not mistaken for typing. */
const programmatic = Annotation.define<boolean>();

/** Colors come from CSS custom properties, so one theme serves light and dark. */
const cueTheme = EditorView.theme({
  '&': { height: '100%', fontSize: '13.5px', color: 'var(--text)', backgroundColor: 'var(--surface)' },
  '.cm-scroller': { fontFamily: 'var(--font-mono)', lineHeight: '1.6' },
  '.cm-content': { padding: '8px 0', caretColor: 'var(--text)' },
  '&.cm-focused': { outline: 'none' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--text)' },
  '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
    { backgroundColor: 'var(--selection)' },
  '.cm-gutters': { backgroundColor: 'var(--surface)', color: 'var(--text-3)', border: 'none' },
  '.cm-activeLine': { backgroundColor: 'var(--active-line)' },
  '.cm-activeLineGutter': { backgroundColor: 'var(--active-line)', color: 'var(--text-2)' },
  '.cm-foldPlaceholder': { backgroundColor: 'var(--surface-2)', border: 'none', color: 'var(--text-2)' },
  '.cm-selectionMatch': { backgroundColor: 'var(--surface-3)' },
  '&.cm-focused .cm-matchingBracket': { backgroundColor: 'var(--surface-3)', outline: '1px solid var(--border)' },
  '.cm-tooltip': { backgroundColor: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' },
  '.cm-tooltip-autocomplete > ul > li[aria-selected]': { backgroundColor: 'var(--accent-soft)', color: 'var(--text)' },
  '.cm-panels': { backgroundColor: 'var(--surface-2)', color: 'var(--text)' },
  // Review. The merge view's own base theme scopes its rules with `&light` / `&dark`, so each override
  // repeats `.cm-editor` to reach the same specificity; themes are emitted after base themes and win the tie.
  // Changed lines get a soft wash and the exact edits a stronger one, with no underlines.
  '&.cm-editor.cm-merge-b .cm-changedLine, &.cm-editor .cm-inlineChangedLine': { backgroundColor: 'var(--diff-add)' },
  '&.cm-editor.cm-merge-b .cm-changedText': { background: 'var(--diff-add-strong)' },
  '&.cm-editor .cm-deletedChunk': { backgroundColor: 'transparent', paddingLeft: '6px' },
  '&.cm-editor .cm-deletedChunk .cm-deletedLine': { backgroundColor: 'var(--diff-del)' },
  // The per-change controls sit on their own row, so they never cover the end of a long line.
  '&.cm-editor .cm-deletedChunk .cm-chunkButtons': {
    position: 'sticky',
    left: '0',
    display: 'flex',
    width: 'max-content',
    padding: '3px 0',
  },
  '&.cm-editor .cm-deletedChunk .cm-deletedText': { background: 'var(--diff-del-strong)' },
  '&.cm-editor .cm-changeGutter': { width: '3px', paddingLeft: '0' },
  '&.cm-editor.cm-merge-b .cm-changedLineGutter': { background: 'var(--success)' },
  '&.cm-editor .cm-deletedLineGutter': { background: 'var(--danger)' },
  '&.cm-editor .cm-collapsedLines': {
    background: 'var(--surface-2)',
    color: 'var(--text-2)',
    fontFamily: 'var(--font-ui)',
    fontSize: 'var(--text-xs)',
  },
  // Per-change Accept and Reject, drawn like the rest of Cue's small buttons.
  '&.cm-editor .cm-chunkButtons button': {
    minHeight: '22px',
    margin: '0 4px 0 0',
    padding: '0 8px',
    border: '1px solid var(--border-strong)',
    borderRadius: '6px',
    background: 'var(--surface)',
    color: 'var(--text)',
    fontFamily: 'var(--font-ui)',
    fontSize: 'var(--text-xs)',
    fontWeight: '600',
    cursor: 'pointer',
  },
  '&.cm-editor .cm-chunkButtons button[name=accept]': { color: 'var(--success)' },
  '&.cm-editor .cm-chunkButtons button[name=reject]': { color: 'var(--danger)' },
  '&.cm-editor .cm-chunkButtons button:hover': { background: 'var(--surface-2)' },
});

const cueHighlight = HighlightStyle.define([
  { tag: [t.comment, t.lineComment, t.blockComment, t.docComment], color: 'var(--hl-comment)', fontStyle: 'italic' },
  { tag: [t.keyword, t.controlKeyword, t.operatorKeyword, t.definitionKeyword, t.moduleKeyword, t.modifier], color: 'var(--hl-keyword)' },
  { tag: [t.string, t.special(t.string), t.regexp, t.escape, t.character], color: 'var(--hl-string)' },
  { tag: [t.number, t.bool, t.null, t.atom, t.unit], color: 'var(--hl-number)' },
  { tag: [t.function(t.variableName), t.function(t.propertyName), t.definition(t.function(t.variableName))], color: 'var(--hl-title)' },
  { tag: [t.className, t.typeName, t.namespace, t.definition(t.typeName)], color: 'var(--hl-type)' },
  { tag: [t.propertyName, t.attributeName], color: 'var(--hl-attr)' },
  { tag: t.tagName, color: 'var(--hl-keyword)', fontWeight: '600' },
  { tag: t.heading, color: 'var(--hl-title)', fontWeight: '650' },
  { tag: t.monospace, fontFamily: 'var(--font-mono)', fontSize: '0.92em' },
  { tag: [t.standard(t.variableName), t.special(t.variableName), t.self], color: 'var(--hl-builtin)' },
  { tag: [t.meta, t.processingInstruction, t.annotation], color: 'var(--hl-comment)' },
  { tag: t.invalid, color: 'var(--danger)' },
  { tag: t.link, textDecoration: 'underline' },
  { tag: t.strong, fontWeight: '700' },
  { tag: t.emphasis, fontStyle: 'italic' },
]);

async function loadLanguage(id: string): Promise<Extension> {
  const info = languageInfo(id);
  if (!info.codemirror) return [];
  const description = LanguageDescription.matchLanguageName(languages, info.codemirror, true);
  if (!description) return [];
  return description.load();
}

type ReviewMode = 'diff' | 'new-file';

export interface EditorOptions {
  doc: string;
  language: string;
  dark: boolean;
  /** `spec` is prose: no line numbers or folding, always wrapped, with a placeholder when empty. */
  variant?: 'code' | 'spec';
  placeholder?: string;
}

export function createEditor(parent: HTMLElement, initial: EditorOptions, callbacks: EditorCallbacks): EditorApi {
  const prose = initial.variant === 'spec';
  const languageConf = new Compartment();
  const darkConf = new Compartment();
  const reviewConf = new Compartment();
  const readOnlyConf = new Compartment();
  const wrapConf = new Compartment();
  // Phones wrap long lines instead of scrolling sideways, so a whole diff fits the screen.
  const narrow = window.matchMedia('(max-width: 799px)');
  const wrapFor = (isNarrow: boolean): Extension => (prose || isNarrow ? EditorView.lineWrapping : []);
  let mode: ReviewMode | null = null;
  let streaming = false;
  /** Kept for new-file reviews, which have no merge view to read the original from. */
  let reviewOriginal = '';
  let lastRemaining = 0;
  let languageToken = 0;

  const selectionOf = (state: EditorState): EditorSelection | null => {
    const range = state.selection.main;
    if (range.empty) return null;
    return {
      from: range.from,
      to: range.to,
      text: state.sliceDoc(range.from, range.to),
      fromLine: state.doc.lineAt(range.from).number,
      toLine: state.doc.lineAt(range.to).number,
    };
  };

  const remainingIn = (state: EditorState): number => {
    if (mode === 'new-file') return 1;
    return getChunks(state)?.chunks.length ?? 0;
  };

  const report = (state: EditorState, byUser = false) => {
    const remaining = mode ? remainingIn(state) : 0;
    lastRemaining = remaining;
    callbacks.onReviewChange({ reviewing: mode !== null, remaining, newFile: mode === 'new-file', streaming, byUser });
  };

  const mergeExtension = (original: string, controls: boolean) =>
    unifiedMergeView({
      original,
      mergeControls: controls,
      highlightChanges: true,
      gutter: true,
      syntaxHighlightDeletions: true,
      collapseUnchanged: controls ? { margin: 3, minSize: 6 } : undefined,
    });

  const view = new EditorView({
    parent,
    state: EditorState.create({
      doc: initial.doc,
      extensions: [
        prose ? [] : [lineNumbers(), highlightActiveLineGutter(), foldGutter()],
        initial.placeholder ? placeholder(initial.placeholder) : [],
        highlightSpecialChars(),
        history(),
        drawSelection(),
        dropCursor(),
        EditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        syntaxHighlighting(cueHighlight),
        bracketMatching(),
        closeBrackets(),
        autocompletion(),
        rectangularSelection(),
        crosshairCursor(),
        prose ? [] : highlightActiveLine(),
        highlightSelectionMatches(),
        keymap.of([
          { key: 'Mod-Enter', run: () => (callbacks.onRun(), true) },
          { key: 'Mod-k', run: () => (callbacks.onInlineEdit(), true) },
          { key: 'F7', run: goToNextChunk },
          { key: 'Shift-F7', run: goToPreviousChunk },
          indentWithTab,
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...completionKeymap,
          ...lintKeymap,
        ]),
        cueTheme,
        darkConf.of(EditorView.darkTheme.of(initial.dark)),
        languageConf.of([]),
        reviewConf.of([]),
        readOnlyConf.of([]),
        wrapConf.of(wrapFor(narrow.matches)),
        EditorView.updateListener.of((update) => {
          const isProgrammatic = update.transactions.some((tr) => tr.annotation(programmatic));
          if (update.docChanged && !isProgrammatic) callbacks.onChange(update.state.doc.toString());
          if (update.selectionSet || update.docChanged) callbacks.onSelectionChange(selectionOf(update.state));
          // Per-chunk Accept and Reject arrive as ordinary transactions from the merge view.
          if (mode === 'diff' && !streaming && !isProgrammatic) {
            const remaining = remainingIn(update.state);
            if (remaining !== lastRemaining) report(update.state, true);
          }
        }),
      ],
    }),
  });

  /** Replace the whole document without recording it as an edit the user can undo into. */
  const replaceQuietly = (doc: string, extra: Partial<Parameters<EditorView['dispatch']>[0]> = {}) => {
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: doc },
      annotations: [programmatic.of(true), Transaction.addToHistory.of(false)],
      ...extra,
    });
  };

  const onNarrowChange = (event: MediaQueryListEvent) => {
    view.dispatch({ effects: wrapConf.reconfigure(wrapFor(event.matches)) });
  };
  narrow.addEventListener('change', onNarrowChange);

  const api: EditorApi = {
    getDoc: () => view.state.doc.toString(),
    setDoc(doc) {
      mode = null;
      streaming = false;
      replaceQuietly(doc, {
        effects: [reviewConf.reconfigure([]), readOnlyConf.reconfigure([])],
        selection: { anchor: 0 },
      });
      report(view.state);
    },
    getSelection: () => selectionOf(view.state),
    async setLanguage(id) {
      const token = ++languageToken;
      const extension = await loadLanguage(id);
      if (token !== languageToken) return;
      view.dispatch({ effects: languageConf.reconfigure(extension) });
    },
    setTheme(dark) {
      view.dispatch({ effects: darkConf.reconfigure(EditorView.darkTheme.of(dark)) });
    },
    startReview(original, proposed) {
      // Leaving a streaming preview first keeps the undo history as original -> proposal.
      if (streaming) api.cancelStream(original);
      mode = original.trim() ? 'diff' : 'new-file';
      reviewOriginal = original;
      if (view.state.doc.toString() !== original) replaceQuietly(original);
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: proposed },
        annotations: programmatic.of(true),
        effects: [reviewConf.reconfigure(mode === 'diff' ? mergeExtension(original, true) : []), readOnlyConf.reconfigure([])],
        selection: { anchor: 0 },
        scrollIntoView: true,
      });
      report(view.state);
    },
    streamReview(original, partial) {
      if (!streaming) {
        streaming = true;
        mode = original.trim() ? 'diff' : 'new-file';
        reviewOriginal = original;
        replaceQuietly(partial, {
          effects: [
            reviewConf.reconfigure(mode === 'diff' ? mergeExtension(original, false) : []),
            readOnlyConf.reconfigure(streamingReadOnly),
          ],
        });
        report(view.state);
      } else {
        replaceQuietly(partial);
      }
      // Follow the end of what has been written so far.
      view.dispatch({ effects: EditorView.scrollIntoView(view.state.doc.length, { y: 'end' }) });
    },
    cancelStream(restore) {
      if (!streaming) return;
      streaming = false;
      mode = null;
      replaceQuietly(restore, { effects: [reviewConf.reconfigure([]), readOnlyConf.reconfigure([])] });
      report(view.state);
    },
    endReview(keep) {
      if (!mode) return view.state.doc.toString();
      const original = mode === 'diff' ? getOriginalDoc(view.state).toString() : reviewOriginal;
      mode = null;
      streaming = false;
      if (keep === 'original') {
        view.dispatch({
          changes: { from: 0, to: view.state.doc.length, insert: original },
          annotations: programmatic.of(true),
          effects: [reviewConf.reconfigure([]), readOnlyConf.reconfigure([])],
        });
      } else {
        view.dispatch({ effects: [reviewConf.reconfigure([]), readOnlyConf.reconfigure([])] });
      }
      report(view.state);
      return view.state.doc.toString();
    },
    isReviewing: () => mode !== null,
    refresh: () => view.requestMeasure(),
    nextChunk: () => void goToNextChunk(view),
    previousChunk: () => void goToPreviousChunk(view),
    focus: () => view.focus(),
    undo: () => void undo(view),
    redo: () => void redo(view),
    destroy: () => {
      narrow.removeEventListener('change', onNarrowChange);
      view.destroy();
    },
  };

  void api.setLanguage(initial.language);
  return api;
}
